import { useStores } from '@/hooks/use-stores';
import { DeviceType } from '@/constants/deviceTypeEnum';

import { Entity } from '../../../NodeWidget.types';
import { areArraysEqual, buildPhysicalSchema, DEFAULT_MODEL_IDS } from '../../EditAssetDrawer.utils';

export const isOfDeviceType = (deviceType: DeviceType, deviceTypes: DeviceType[]) => deviceTypes.includes(deviceType);

export const isCentrifugalCompressor = (deviceType: DeviceType) =>
  isOfDeviceType(deviceType, [DeviceType.Centrifugal, DeviceType.CentrifugalAndPowerSensor]);

export const isFixedSpeedCompressor = (deviceType: DeviceType) =>
  isOfDeviceType(deviceType, [DeviceType.FixedSpeed, DeviceType.FixedSpeedAndPowerSensor]);

export const useInitialValues = (entity: Entity) => {
  const { devicesStore, pidEditStore } = useStores();
  const {
    name,
    deviceId,
    deviceModelId,
    controllerId,
    serialNumber,
    compressorNumber,
    bandsControl,
    fiveLoop,
    deviceType,
  } = entity;

  const connectTo = pidEditStore.getDraftDeviceWithoutPipesByIdToDevices(deviceId);
  const initialDeviceModelId = deviceModelId ?? DEFAULT_MODEL_IDS.dummyCompressor;

  return {
    name,
    deviceModelId: initialDeviceModelId,
    controllerId: controllerId ?? devicesStore.getDeviceControllerId(deviceId),
    serialNumber,
    compressorNumber,
    bandsControl,
    fiveLoop,
    connectTo,
    deviceType,
  };
};

export const mapFormValuesToPayload = (values, entity, initialValues) => ({
  name: values.name,
  deviceModelId: values.deviceModelId,
  controllerId: values.controllerId,
  deviceType: values.deviceType,
  attributes: {
    compressorNumber: values.compressorNumber,
    serialNumber: values.serialNumber,
    bandsControl: values.bandsControl,
    fiveLoop: values.fiveLoop,
  },
  ...(entity?.deviceId
    ? !areArraysEqual(initialValues.connectTo, values.connectTo) && {
        physicalSchema: buildPhysicalSchema(entity, {
          toDevices: values.connectTo,
        }),
      }
    : {
        to: values.connectTo,
      }),
});

export const useIsSerialNumberReadOnly = (serialNumber: string) => {
  const { stationStore } = useStores();
  return !!stationStore.assetsDevices?.find(device => device.serialNumber === serialNumber);
};

export const mapUnusedSerialNumbersToOptions = (serialNumbers: string[]) => {
  if (serialNumbers?.length) {
    const mappedSerialNumbers = serialNumbers.map(serialNumber => ({
      label: serialNumber,
      value: serialNumber,
    }));
    return mappedSerialNumbers;
  }
  return [];
};

export const getCompressorTypeOptions = t => [
  { value: DeviceType.Centrifugal, label: t('Centrifugal') },
  { value: DeviceType.FixedSpeed, label: t('Fixed Speed') },
  { value: DeviceType.VariableSpeedDrive, label: t('Variable Speed Drive') },
];
